import DOMPurify from 'dompurify';

/**
 * sanitizeHtml — Permissive sanitizer for rich CMS content.
 *
 * Allows safe structural and formatting HTML tags (headings, paragraphs,
 * lists, links, images, tables, code blocks) as authored in Orchard Core CMS.
 *
 * Strips all executable content:
 *   - <script>, <iframe>, <object>, <embed>, <form>
 *   - Event handler attributes (onclick, onerror, onload, etc.)
 *   - javascript: URIs in href/src
 *
 * Use for: Guides page HTML body, Changelog descriptions,
 *           Workflow step descriptions, Search result previews,
 *           API parameter descriptions from OpenAPI spec.
 */
export const sanitizeHtml = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'strong', 'b', 'em', 'i', 'u', 'sub', 'sup',
      'ul', 'ol', 'li',
      'a',
      'code', 'pre', 'blockquote',
      'span', 'div',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
      'img',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'class', 'target', 'rel', 'title', 'style',
      'width', 'height',
    ],
  });
};

/**
 * sanitizeHtmlStrict — Strict sanitizer for short text snippets.
 *
 * Only allows basic inline formatting — no headings, no links, no images.
 *
 * Use for: API parameter descriptions rendered inline,
 *           any short user-facing text that must not contain block elements.
 */
export const sanitizeHtmlStrict = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'code', 'span', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
  });
};

/**
 * stripTags — Removes all HTML tags from a plain-text string.
 *
 * NOT a DOMPurify call — this is intentionally a simple regex strip for strings
 * that should never contain any HTML at all (e.g. search query parameters,
 * support ticket title/description sent as plain text to the backend API,
 * workflow step titles, code snippet strings from CMS).
 *
 * Use for: Navbar search query before URL construction,
 *           Support ticket title and description before POST body,
 *           Workflow plain-text fields: stepTitle, description, displayText,
 *           CMS codeSnippet field before rendering in code panels.
 */
export const stripTags = (str) => {
  if (!str) return '';
  // Strip actual HTML tags: <script>...</script>
  let result = str.replace(/<[^>]*>/g, '');
  // Strip HTML-entity-encoded tags: <script>...</script>
  result = result.replace(/<[^&]*>/g, '');
  return result;
};

/**
 * sanitizeHtmlDecoded — Sanitizes HTML that may contain entity-encoded tags.
 *
 * Some CMS fields encode injected content as HTML entities (e.g. <script>)
 * rather than raw tags. Standard DOMPurify does not strip entity-encoded tags
 * because they are already safe as text — but they display as visible tag text.
 *
 * This function decodes the HTML string first using DOMParser, then applies
 * DOMPurify sanitization to remove any real tags that were encoded.
 *
 * Use for: Workflow step descriptions from Orchard Core CMS where injected
 *           content may be entity-encoded.
 */
export const sanitizeHtmlDecoded = (dirty) => {
  if (!dirty) return '';
  // Use DOMParser to decode HTML entities into actual characters
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  const decoded = doc.documentElement.textContent;
  // Now apply DOMPurify on the decoded string
  return DOMPurify.sanitize(decoded, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'strong', 'b', 'em', 'i', 'u', 'sub', 'sup',
      'ul', 'ol', 'li',
      'a',
      'code', 'pre', 'blockquote',
      'span', 'div',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
      'img',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'class', 'target', 'rel', 'title', 'style',
      'width', 'height',
    ],
  });
};